/**
 * RBAC (Role-Based Access Control) Configuration
 * 
 * Centralized configuration for all RBAC features and app names.
 * 
 * Usage:
 *   import { RBAC_APPS, UplGenieFeatures } from './constant/rbacConfig';
 *   hasFeatureByKey(RBAC_APPS.CHAT_GENIE, UplGenieFeatures.DOC_UPLOAD)
 */

// ============================================
// App Names
// ============================================
export const RBAC_APPS = {
  CHAT_GENIE: 'Chat Genie',
  TRANSLATION: 'Translate AI',
  PROCUREMENT: 'Procurement AI',
  GMT: 'GMT',
  HR_ASSISTANT: 'HR Assistant',
  DEEP_INSIGHTS: 'Deep Insights',
  TALENT_PULSE_360: 'Talent Pulse 360',
};

// ============================================
// API App Name to Display Name Mapping
// Maps the app names from API response to display names (must match appConfig labels)
// ============================================
export const API_APP_NAME_MAP = {
  'aichat': 'Chat Genie',
  'lgtrans': 'Translate AI',
  'procai': 'Procurement AI',
  'gmtai': 'GMT',
  'hrassistant': 'HR Assistant',
  'deepinsights': 'Deep Insights',
  'talentpulse360': 'Talent Pulse 360',
};

// Reverse mapping: Display name to API app name
export const DISPLAY_NAME_TO_API_MAP = Object.fromEntries(
  Object.entries(API_APP_NAME_MAP).map(([key, value]) => [value, key])
);

// ============================================
// Feature Constants
// ============================================

// Chat Genie Features
export const CHAT_GENIE_FEATURES = {
  DOC_UPLOAD: 'doc_upload',
  ADVANCED_MODAL: 'advance_model',
  WEB_SEARCH: 'web_search',
  AUTO_MODEL_SELECTION: 'auto_select',
};

// Translation Features
export const TRANSLATION_FEATURES = {
  DOC_TRANSLATION: 'doc_translation',
  TEXT_TRANSLATION: 'text_translation',
};

// Procurement Features (add when needed)
export const PROCUREMENT_FEATURES = {};

// GMT Features (add when needed)
export const GMT_FEATURES = {};

// HR Assistant Features (add when needed)
export const HR_ASSISTANT_FEATURES = {};

// Deep Insights Features
export const DEEP_INSIGHTS_FEATURES = {
  CHAT: 'deepinsights_chat',
};

// Talent Pulse 360 Feature
export const TALENT_PULSE_360_FEATURE = {};


// ============================================
// Combined Features Map
// ============================================
export const RBAC_FEATURES = {
  [RBAC_APPS.CHAT_GENIE]: CHAT_GENIE_FEATURES,
  [RBAC_APPS.TRANSLATION]: TRANSLATION_FEATURES,
  [RBAC_APPS.PROCUREMENT]: PROCUREMENT_FEATURES,
  [RBAC_APPS.GMT]: GMT_FEATURES,
  [RBAC_APPS.HR_ASSISTANT]: HR_ASSISTANT_FEATURES,
  [RBAC_APPS.DEEP_INSIGHTS]: DEEP_INSIGHTS_FEATURES,
  [RBAC_APPS.TALENT_PULSE_360]: TALENT_PULSE_360_FEATURE,
};

// ============================================
// Helper Function (used by useRbac hook)
// ============================================
export const getFeatureValue = (appName, featureKey) => {
  const appFeatures = RBAC_FEATURES[appName];
  if (!appFeatures) return null;
  return appFeatures[featureKey] || null;
};

// ============================================
// Convenience Exports (for easier imports)
// ============================================
export const UplGenieFeatures = CHAT_GENIE_FEATURES;
export const TranslationFeatures = TRANSLATION_FEATURES;
